/**
 * F06 — narrow re-exports + admin-only DTO shapes the admin app uses across
 * pages. Most types live in `@bravemove/types`; this file documents the local
 * additions (API error envelope + minimal session helpers).
 */
import type {
  AdminAuditEntry,
  AdminRole,
  AdminSession,
  AdminUser,
  AdminUserSummary,
  ActiveCurrencyRates,
  CurrencyRate,
  LoginResponse,
  Permission,
} from '@bravemove/types';

export type {
  AdminAuditEntry,
  AdminRole,
  AdminSession,
  AdminUser,
  AdminUserSummary,
  ActiveCurrencyRates,
  CurrencyRate,
  LoginResponse,
  Permission,
};

/** Standard error envelope returned by every /admin/** route. */
export interface ApiErrorBody {
  readonly error: {
    readonly code: string;
    readonly message?: string;
    readonly details?: Record<string, unknown>;
    readonly issues?: unknown;
    readonly previousRate?: number;
    readonly newRate?: number;
    readonly deltaPct?: number;
    readonly min?: number;
    readonly max?: number;
    readonly attempted?: number;
  };
}

/** Thrown by `adminFetch` on non-2xx responses. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly body: ApiErrorBody | null;

  constructor(status: number, body: ApiErrorBody | null, message?: string) {
    super(message ?? body?.error?.message ?? `Request failed (${status})`);
    this.name = 'ApiError';
    this.status = status;
    this.code = body?.error?.code ?? 'UNKNOWN_ERROR';
    this.body = body;
  }
}

/** Response shape from POST /admin/auth/totp/enroll. */
export interface TotpEnrollResponse {
  readonly otpauthUrl: string;
  readonly qrDataUrl: string;
  readonly manualSecret: string;
}

/** Response shape from GET /admin/auth/invite/:token. */
export interface InvitePreviewResponse {
  readonly email: string;
  readonly role: AdminRole;
  readonly displayName: string | null;
}

/** Response shape from POST /admin/auth/invite/accept. */
export interface AcceptInviteResponse {
  readonly enrollmentToken: string;
  readonly enrollmentExpiresAt: string;
}

/** Page envelope used by /admin/users and /admin/audit-log. */
export interface ListEnvelope<T> {
  readonly items: readonly T[];
  readonly total?: number;
  readonly limit: number;
  readonly offset: number;
}
